import { BaseLanguageCode, LocaleCode } from '@/consts/i18n';

const localeValues = Object.values(LocaleCode);

// Returns the first locale that starts with the given base language code, or undefined if none is found
// - relies on the order of const/i18n/LocaleCode to represent our order of preference
// - e.g. `pt-BR` over `pt-PT` or `en` over `en-GB`
export function languageToLocale(language: BaseLanguageCode): LocaleCode | undefined {
  return localeValues.find(locale => locale.startsWith(language));
}

export function localeToLanguage(locale: LocaleCode): BaseLanguageCode | undefined {
  const base = locale.split('-')[0] as keyof typeof BaseLanguageCode;
  return BaseLanguageCode[base];
}

export function tryParseLocaleCode(locale: string | undefined): LocaleCode | undefined {
  if (!locale) return undefined;

  if (locale in LocaleCode) {
    return LocaleCode[locale as keyof typeof LocaleCode];
  }

  if (locale in BaseLanguageCode) {
    return languageToLocale(locale as BaseLanguageCode);
  }

  // Handle case-normalised or region-variant locales via Intl canonicalisation
  // e.g. "en-gb" → "en-GB", "es-MX" → "es-MX" (base "es" is a known language)
  // Locales whose base language is not in BaseLanguageCode are still rejected
  try {
    const [canonical] = Intl.getCanonicalLocales(locale);
    if (canonical in LocaleCode) {
      return LocaleCode[canonical as keyof typeof LocaleCode];
    }

    return canonical as LocaleCode;
  } catch {
    // invalid BCP 47 format
  }

  return undefined;
}

export function setUserLocaleCookie(userLocale: string | undefined): void {
  const desiredLocale = tryParseLocaleCode(userLocale);

  if (!desiredLocale) {
    return;
  }

  // Ensure we set the cookie on the root domain so that it is accessible across all subdomains
  // - e.g. we set on statsbomb.[thor]hudl.com but we want it to be accessible on ident.[thor]hudl.com
  const parts = window.location.hostname.split('.');
  const domain = parts.length > 2 ? parts.slice(-2).join('.') : window.location.hostname;

  // 14 days (matches IQ Classic / hudl.com)
  const maxAgeSeconds = 60 * 60 * 24 * 14;

  document.cookie = `locale-des=${desiredLocale}; Domain=${domain}; Path=/; Max-Age=${maxAgeSeconds}; Secure; SameSite=Lax`;
}
